import { useEffect, useRef, useState } from "react";
import { FarmaClassLogo } from "@/components/FarmaClassLogo";
import { BookingBanner } from "@/components/BookingBanner";
import { Link } from "react-router-dom";
import {
  ArrowRight, Upload, Gauge, Truck, Globe2, Users, FileBarChart,
  TrendingUp, ShieldCheck, Phone, Mail as MailIcon, Boxes, Check, FileText,
} from "lucide-react";
import { RequestInfoForm } from "@/components/RequestInfoModal";
import { PresentationVideo } from "@/components/PresentationVideo";
import { TestimonialsMarquee, REVIEWS } from "@/components/TestimonialsMarquee";
import { RoiSection } from "@/components/landing/RoiSection";
import { DossierGate, hasDossierLead } from "@/components/landing/DossierGate";

const ARCHIDUQUE_URL = "https://archiduqueconsulting.com/nosotros/";

const FEATURES = [
  {
    icon: Gauge,
    title: "Semáforo por molécula",
    text: "Cada conjunto homogéneo clasificado en verde, ámbar o rojo según tu grado de dispensación de genérico.",
  },
  {
    icon: TrendingUp,
    title: "Top 25 de pérdidas",
    text: "Las 25 moléculas donde más dinero dejas en marca, con el importe recuperable y la evolución mes a mes.",
  },
  {
    icon: Truck,
    title: "Mejor proveedor por molécula",
    text: "Carga las condiciones de tus laboratorios y FarmaClass te dice dónde comprar cada EFG para ganar más.",
  },
  {
    icon: Globe2,
    title: "Benchmark real",
    text: "Compara tu cuota de genérico con la media de tu provincia, tu comunidad y del resto de España.",
  },
  {
    icon: Users,
    title: "Análisis por empleado",
    text: "Quién sustituye y quién no, con fichas individuales para trabajar la conversión en el mostrador.",
  },
  {
    icon: Boxes,
    title: "Stock, faltas y mínimos",
    text: "Moléculas sin genérico en stock, propuesta de pedido y mínimos/máximos según tu venta media diaria.",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Sube tu fichero de dispensación",
    text: "Excel, TXT o CSV directo de tu programa de gestión. Detectamos el formato solo, también los desgloses por empleado.",
  },
  {
    n: "02",
    title: "FarmaClass lo clasifica",
    text: "Separa marca de genérico molécula a molécula, calcula tu cuota real en unidades y en euros y semaforiza cada G.H.",
  },
  {
    n: "03",
    title: "Recibes el plan de acción",
    text: "Resumen del mes, top de pérdidas, pedidos listos para enviar al proveedor e informes PDF para tu equipo.",
  },
];

export default function LandingPage() {
  const [formInView, setFormInView] = useState(false);
  const [dossierOpen, setDossierOpen] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    document.title = "FarmaClass · Analítica EFG para farmacias";
  }, []);

  const goToForm = () => {
    setFormInView(true);
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="landing-page">
      {/* NAV */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <FarmaClassLogo className="h-9 sm:h-11" testId="landing-farmaclass-logo" />
          <div className="flex items-center gap-2 sm:gap-3">
            <a
              href={ARCHIDUQUE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex text-xs sm:text-sm font-bold text-slate-700 hover:text-slate-950 px-2 sm:px-3 py-2 whitespace-nowrap"
              data-testid="landing-nav-about"
            >
              Quiénes somos
            </a>
            <Link
              to="/login"
              className="text-xs sm:text-sm font-bold text-slate-700 hover:text-slate-950 px-2 sm:px-3 py-2 whitespace-nowrap"
              data-testid="landing-login-link"
            >
              Ya soy cliente
            </Link>
            <button
              onClick={goToForm}
              className="hidden sm:inline-flex bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-lg text-sm font-bold transition-colors whitespace-nowrap"
              data-testid="landing-nav-cta"
            >
              Solicitar información
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative bg-slate-950 text-white overflow-hidden">
        <img
          src="/farmaclass-hero.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-center scale-105 blur-[1.5px] opacity-55"
          data-testid="landing-hero-bg"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/70 to-slate-950/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/60" />
        <div className="absolute inset-0 opacity-[0.16] bg-[radial-gradient(circle_at_75%_25%,#34d399,transparent_45%),radial-gradient(circle_at_15%_80%,#0ea5e9,transparent_40%)]" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-28 lg:py-32">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] font-bold text-emerald-300 border border-emerald-400/30 bg-emerald-400/10 rounded-full px-3 py-1.5">
              <ShieldCheck className="h-3.5 w-3.5" /> Archiduque Consulting
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-6 leading-[1.05]">
              Dispensa más genérico
              <span className="block text-emerald-300">sin perder ni un cliente.</span>
            </h1>
            <p className="text-base md:text-lg text-slate-300 mt-6 max-w-2xl leading-relaxed">
              FarmaClass analiza la dispensación de tu farmacia, separa marca de genérico molécula a
              molécula y te dice exactamente dónde estás perdiendo dinero y cómo recuperarlo.
            </p>
            <div className="mt-9 flex flex-wrap gap-3">
              <button
                onClick={goToForm}
                className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 px-6 py-3.5 rounded-xl font-bold inline-flex items-center gap-2 transition-colors"
                data-testid="landing-hero-cta"
              >
                Solicitar información <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
              </button>
              <Link
                to="/login"
                className="bg-white/10 border border-white/20 hover:bg-white/20 text-white px-6 py-3.5 rounded-xl font-bold inline-flex items-center gap-2 transition-colors"
                data-testid="landing-hero-login"
              >
                Acceder a mi cuenta
              </Link>
            </div>
            <ul className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-300">
              {["Excel · TXT · CSV de cualquier programa", "Sin instalar nada", "Datos de tu farmacia cifrados"].map((t) => (
                <li key={t} className="inline-flex items-center gap-2">
                  <Check className="h-4 w-4 text-emerald-400" strokeWidth={2.6} /> {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* RESERVA CITA */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 pt-12 -mb-4" data-testid="landing-booking-section">
        <BookingBanner />
      </section>

      {/* CÓMO FUNCIONA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="max-w-2xl">
          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-emerald-700">Cómo funciona</div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-3">
            Tres pasos, un informe que se entiende.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
          {STEPS.map((s, i) => (
            <div key={s.n} className="relative border-l-2 border-emerald-500/40 pl-5" data-testid={`landing-step-${i + 1}`}>
              <div className="font-display text-4xl font-extrabold text-slate-200 font-mono-num leading-none">{s.n}</div>
              <h3 className="text-base md:text-lg font-bold mt-3">{s.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BANNER DOSSIER DE RENTABILIDAD */}
      <section className="bg-emerald-600" data-testid="landing-dossier-banner">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start sm:items-center gap-3">
            <FileText className="h-6 w-6 text-emerald-950 shrink-0 mt-0.5 sm:mt-0" strokeWidth={1.8} />
            <p className="font-display text-base md:text-lg font-extrabold text-emerald-950 leading-snug">
              ¿Quieres ver las rentabilidades de las farmacias que ya usan FarmaClass?
            </p>
          </div>
          <a
            href="/dossier-rentabilidad-farmaclass.pdf"
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              if (hasDossierLead()) return;
              e.preventDefault();
              setDossierOpen(true);
            }}
            className="shrink-0 inline-flex items-center justify-center gap-2 bg-slate-950 hover:bg-slate-800 text-white px-5 py-3 rounded-xl font-bold text-sm transition-colors"
            data-testid="landing-dossier-cta"
          >
            Ver el dossier <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
          </a>
        </div>
      </section>
      <DossierGate open={dossierOpen} onClose={() => setDossierOpen(false)} />

      {/* RENTABILIDAD DE LA INVERSIÓN */}
      <RoiSection onCta={goToForm} />

      {/* IMAGEN + CLAIM */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <div className="w-full rounded-2xl overflow-hidden bg-white border border-slate-200 shadow-sm">
            <img
              src="/farmaclass-campana.jpg"
              alt="FarmaClass · panel de rentabilidad y equipo de farmacia"
              className="w-full h-auto object-contain block"
              loading="lazy"
              data-testid="landing-photo"
            />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-emerald-700">El dinero invisible</div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-3">
              Cada caja de marca que sale por la puerta tiene un coste que no ves.
            </h2>
            <p className="text-base text-slate-600 mt-5 leading-relaxed">
              No es dispensar peor: es dispensar con datos. FarmaClass te enseña, molécula a molécula,
              cuántas unidades podrías haber convertido a EFG, cuánto habrías ganado con las condiciones
              de tus laboratorios y qué moléculas se te están escapando este mes.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Cuota real de genérico en unidades y en euros",
                "Importe recuperable por molécula y por empleado",
                "Comparativa con tu provincia y con toda España",
                "Informes PDF listos para la reunión de equipo",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-slate-800">
                  <Check className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" strokeWidth={2.6} /> {t}
                </li>
              ))}
            </ul>
            <button
              onClick={goToForm}
              className="mt-8 inline-flex items-center gap-2 bg-slate-950 hover:bg-slate-800 text-white px-5 py-3 rounded-xl text-sm font-bold transition-colors"
              data-testid="landing-mid-cta"
            >
              Quiero verlo con mis datos <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <div className="max-w-2xl">
          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-emerald-700">Qué incluye</div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-3">
            Todo lo que necesitas para subir tu cuota de EFG.
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="border border-slate-200 rounded-2xl p-6 transition-all duration-200 hover:border-emerald-300 hover:shadow-lg hover:-translate-y-0.5"
              data-testid={`landing-feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <f.icon className="h-5 w-5 text-emerald-600" strokeWidth={1.8} />
              <h3 className="text-base md:text-lg font-bold mt-4">{f.title}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* RESEÑAS DE CLIENTES */}
      <section className="bg-slate-50 border-t border-slate-200" data-testid="landing-reviews-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-16 sm:pt-20 pb-14 sm:pb-16">
          <div className="max-w-2xl">
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-emerald-700">Lo dicen ellas</div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-3">
              Farmacias que ya trabajan con FarmaClass.
            </h2>
            <p className="text-base text-slate-600 mt-4 leading-relaxed">
              {REVIEWS.length === 1
                ? "La primera de muchas: así lo cuenta quien lo usa cada día en el mostrador."
                : "Así lo cuentan quienes lo usan cada día en el mostrador."}
            </p>
          </div>
        </div>
        <TestimonialsMarquee />
        <div className="h-14 sm:h-16" />
      </section>

      {/* VÍDEO DE PRESENTACIÓN */}
      <section className="bg-slate-50 border-t border-slate-200" data-testid="landing-video-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-emerald-700">En 2 minutos</div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mt-3">
            Míralo antes de decidir.
          </h2>
          <p className="text-base text-slate-600 mt-4 max-w-2xl mx-auto leading-relaxed">
            Empieza por la presentación y, si quieres ver lo que hace FarmaClass por dentro,
            cambia al segundo vídeo.
          </p>
          <PresentationVideo className="mt-10" size="lg" testid="landing-video" />
        </div>
      </section>

      {/* FORMULARIO */}
      <section
        ref={formRef}
        id="solicitar"
        className="bg-slate-950 text-white scroll-mt-16"
        data-testid="landing-form-section"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
          <div>
            <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-emerald-300">Sin compromiso</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight mt-3">
              Solicita información
            </h2>
            <p className="text-slate-300 mt-5 leading-relaxed">
              Déjanos tus datos y te contamos cómo funciona con la dispensación de tu farmacia.
              Te contactamos en menos de 24 horas, sin compromiso y sin instalar nada.
            </p>
            <div className="mt-8 space-y-3">
              <a href="tel:+34609467788" className="flex items-center gap-3 text-slate-200 hover:text-white transition-colors" data-testid="landing-phone">
                <Phone className="h-4 w-4 text-emerald-400" /> <span className="font-mono-num">609 46 77 88</span>
              </a>
              <a href="mailto:pedro@archiduquecarlos.com" className="flex items-center gap-3 text-slate-200 hover:text-white transition-colors" data-testid="landing-email">
                <MailIcon className="h-4 w-4 text-emerald-400" /> pedro@archiduquecarlos.com
              </a>
              <div className="flex items-center gap-3 text-slate-400 text-sm">
                <Upload className="h-4 w-4 text-emerald-400" /> Te ayudamos a sacar el fichero de tu programa
              </div>
            </div>
          </div>
          <div className="bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="landing-form-card">
            <div className="px-6 pt-6">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-700">FarmaClass</div>
              <h3 className="font-display text-2xl font-extrabold tracking-tight text-slate-950 mt-1">
                Cuéntanos quién eres
              </h3>
            </div>
            <RequestInfoForm source="landing" />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-2.5 text-center sm:text-left">
            <FileBarChart className="h-4 w-4 shrink-0" />
            <span>
              FarmaClass · Analítica EFG para farmacias ·{" "}
              <a
                href={ARCHIDUQUE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-bold text-slate-800 hover:text-emerald-700 underline decoration-slate-300 hover:decoration-emerald-600 underline-offset-2"
                data-testid="landing-footer-about"
              >
                Archiduque Consulting
              </a>
            </span>
          </div>
          <Link to="/login" className="font-bold text-slate-800 hover:text-slate-950" data-testid="landing-footer-login">
            Acceso clientes →
          </Link>
        </div>
      </footer>

      {/* CTA flotante en móvil */}
      <button
        onClick={goToForm}
        className={`sm:hidden fixed bottom-4 left-4 right-4 z-40 bg-emerald-600 text-white py-3.5 rounded-xl font-bold shadow-xl transition-opacity ${formInView ? "opacity-0 pointer-events-none" : "opacity-100"}`}
        data-testid="landing-sticky-cta"
      >
        Solicitar información
      </button>
    </div>
  );
}
